import { Bath, Car, Droplets, Package, Scissors, Snowflake, Sparkles, Trees, Zap } from 'lucide-react';
import type { BusinessTemplate } from '@/types';
import { CATEGORY_SETS } from './categories';
import { PRESET_SETS } from './presets';
import { SERVICE_SETS } from './services';
import { TERM_SETS } from './terms';

/**
 * ════════════════════════════════════════════════════════════════════════
 *  THE EXTENSION POINT OF QUOTYLAB
 * ════════════════════════════════════════════════════════════════════════
 *  This file is composition only — identity, colour token, illustration
 *  token and pointers into the category/service/terms registries. Adding a
 *  vertical (Photography, Pest Control, Painting, Bike Service…) means:
 *
 *  Prices in the seeded catalogs are SUGGESTED DEFAULTS in US dollars, taken
 *  from the pricing workbook in `data/pricing/` by `npm run import:pricing`
 *  (services the workbook could not price keep the value in their template
 *  file). They are a starting point, not a recommendation: the owner edits them
 *  from the Services page, and editing a quote line never writes back here.
 *
 *  Quote numbering is deliberately NOT part of a template: every business uses
 *  the single house prefix from `config/brand.ts`. A per-business custom prefix,
 *  if it is ever wanted, belongs on the business profile with a UI behind it —
 *  not as silent configuration a vertical inherits.
 *
 *    1. templates/categories/<id>.ts   — its categories
 *    2. templates/services/<id>.ts     — its services
 *    3. templates/presets/index.ts     — its starter bundles (optional)
 *    4. templates/terms.ts             — its default terms
 *    5. one object below
 *
 *  No UI component, route, provider or stylesheet changes. Colours come from
 *  `config/colors.ts`, icons from `config/serviceIcons.ts`, units from
 *  `config/units.ts` and artwork from `templates/illustrations`.
 * ════════════════════════════════════════════════════════════════════════
 */
export const BUSINESS_TEMPLATES: readonly BusinessTemplate[] = [
  {
    id: 'car-detailing',
    name: 'Car Detailing',
    subtitle: 'Car wash, detailing, ceramic coating & paint protection',
    description: 'Priced per vehicle with premium protection packages ready to quote on the forecourt.',
    icon: Car,
    illustration: 'car-detailing',
    color: 'indigo',
    defaultTaxRate: 18,
    recommendedUnits: ['each', 'seat', 'hour', 'session'],
    categories: CATEGORY_SETS['car-detailing'] ?? [],
    services: SERVICE_SETS['car-detailing'] ?? [],
    presets: PRESET_SETS['car-detailing'] ?? [],
    defaultTerms: TERM_SETS['car-detailing'] ?? [],
  },
  {
    id: 'pressure-washing',
    name: 'Pressure Washing',
    subtitle: 'Exterior cleaning and pressure washing',
    description: 'Area-based pricing so square-footage quotes take seconds on site.',
    icon: Droplets,
    illustration: 'pressure-washing',
    color: 'blue',
    defaultTaxRate: 18,
    recommendedUnits: ['sqft', 'linear_ft', 'sqm', 'meter', 'each'],
    categories: CATEGORY_SETS['pressure-washing'] ?? [],
    services: SERVICE_SETS['pressure-washing'] ?? [],
    presets: PRESET_SETS['pressure-washing'] ?? [],
    defaultTerms: TERM_SETS['pressure-washing'] ?? [],
  },
  {
    id: 'plumbing',
    name: 'Plumbing',
    subtitle: 'Repairs, maintenance and installation',
    description: 'Call-out rates, fittings and emergency visits priced and ready.',
    icon: Bath,
    illustration: 'plumbing',
    color: 'teal',
    defaultTaxRate: 18,
    recommendedUnits: ['each', 'linear_ft', 'drain', 'hour', 'meter', 'visit'],
    categories: CATEGORY_SETS['plumbing'] ?? [],
    services: SERVICE_SETS['plumbing'] ?? [],
    presets: PRESET_SETS['plumbing'] ?? [],
    defaultTerms: TERM_SETS['plumbing'] ?? [],
  },
  {
    id: 'electrician',
    name: 'Electrician',
    subtitle: 'Electrical repairs and installations',
    description: 'Point-by-point pricing for fittings, wiring runs and inspections.',
    icon: Zap,
    illustration: 'electrician',
    color: 'amber',
    defaultTaxRate: 18,
    recommendedUnits: ['each', 'hour', 'sqft', 'visit'],
    categories: CATEGORY_SETS['electrician'] ?? [],
    services: SERVICE_SETS['electrician'] ?? [],
    presets: PRESET_SETS['electrician'] ?? [],
    defaultTerms: TERM_SETS['electrician'] ?? [],
  },
  {
    id: 'hvac',
    name: 'HVAC',
    subtitle: 'AC installation and servicing',
    description: 'Unit-based service, installation and repair pricing out of the box.',
    icon: Snowflake,
    illustration: 'hvac',
    color: 'cyan',
    defaultTaxRate: 18,
    recommendedUnits: ['each', 'recharge', 'zone', 'visit', 'hour'],
    categories: CATEGORY_SETS['hvac'] ?? [],
    services: SERVICE_SETS['hvac'] ?? [],
    presets: PRESET_SETS['hvac'] ?? [],
    defaultTerms: TERM_SETS['hvac'] ?? [],
  },
  {
    id: 'lawn-care',
    name: 'Lawn Care',
    subtitle: 'Gardening and landscaping',
    description: 'Area and hourly pricing for maintenance rounds and one-off tidy-ups.',
    icon: Trees,
    illustration: 'lawn-care',
    color: 'green',
    defaultTaxRate: 18,
    recommendedUnits: ['visit', 'application', 'linear_ft', 'cubic_yard', 'sqm', 'sqft', 'hour', 'meter', 'each'],
    categories: CATEGORY_SETS['lawn-care'] ?? [],
    services: SERVICE_SETS['lawn-care'] ?? [],
    presets: PRESET_SETS['lawn-care'] ?? [],
    defaultTerms: TERM_SETS['lawn-care'] ?? [],
  },
  {
    id: 'home-cleaning',
    name: 'Home Cleaning',
    subtitle: 'Residential & commercial cleaning',
    description: 'Room-by-room and deep-clean packages priced for instant quoting.',
    icon: Sparkles,
    illustration: 'home-cleaning',
    color: 'violet',
    defaultTaxRate: 18,
    recommendedUnits: ['each', 'property', 'sqft', 'hour', 'sqm', 'visit'],
    categories: CATEGORY_SETS['home-cleaning'] ?? [],
    services: SERVICE_SETS['home-cleaning'] ?? [],
    presets: PRESET_SETS['home-cleaning'] ?? [],
    defaultTerms: TERM_SETS['home-cleaning'] ?? [],
  },
  {
    id: 'salon-spa',
    name: 'Salon & Spa',
    subtitle: 'Beauty & grooming services',
    description: 'Per-session pricing for hair, skin and spa treatments.',
    icon: Scissors,
    illustration: 'salon',
    color: 'pink',
    defaultTaxRate: 18,
    recommendedUnits: ['session', 'hour', 'each'],
    categories: CATEGORY_SETS['salon-spa'] ?? [],
    services: SERVICE_SETS['salon-spa'] ?? [],
    presets: PRESET_SETS['salon-spa'] ?? [],
    defaultTerms: TERM_SETS['salon-spa'] ?? [],
  },
  {
    id: 'other',
    name: 'Other Service Business',
    subtitle: 'Create your own services',
    description: 'Start from a blank catalog and build pricing that fits your trade.',
    icon: Package,
    illustration: 'toolbox',
    color: 'slate',
    defaultTaxRate: 0,
    recommendedUnits: ['each', 'hour', 'day', 'visit', 'custom'],
    categories: CATEGORY_SETS['other'] ?? [],
    services: SERVICE_SETS['other'] ?? [],
    presets: PRESET_SETS['other'] ?? [],
    defaultTerms: TERM_SETS['other'] ?? [],
  },
];
